/**
 * What the attorney saw when they last opened the Terminology panel, and which
 * terms differ from it.
 *
 * Kept in the browser, per viewer and per draft; nothing of it is stored on
 * the server. The view's address covers every served term's name, definition
 * and numeral, so an unchanged address means nothing to flag. When it moved,
 * each term is compared on those same three, so a flag explains every move of
 * the address that left a row to carry it. A missing or unreadable record
 * flags nothing.
 */
import type {
  ShownTerms,
  Term,
  TermGroupKind,
  TermsResponse,
} from "@patent-studio/api-types";

/** The address last seen, and each term's words then, keyed by group and id. */
export interface SeenTerms {
  address: string;
  rows: Record<string, string>;
}

export type TermFlag = "New" | "Changed";

const STORE_PREFIX = "praxis.terms-seen.";

function rowKey(kind: TermGroupKind, term: Term): string {
  return `${kind}:${term.thing_id}`;
}

/** The words the address covers, as one string to compare. */
export function wordsOf(term: Term): string {
  return JSON.stringify([term.name, term.definition, term.numeral]);
}

export function seenOf(view: TermsResponse): SeenTerms {
  const rows: Record<string, string> = {};
  for (const group of view.groups) {
    for (const term of group.terms)
      rows[rowKey(group.kind, term)] = wordsOf(term);
  }
  return { address: view.address, rows };
}

/** "New" for a term not seen before, "Changed" for one whose words differ, else nothing. */
export function flagOf(
  kind: TermGroupKind,
  term: Term,
  seen: SeenTerms | null,
): TermFlag | null {
  if (seen === null) return null;
  const key = rowKey(kind, term);
  if (!Object.hasOwn(seen.rows, key)) return "New";
  return seen.rows[key] === wordsOf(term) ? null : "Changed";
}

/** Whether any term would be flagged against what was seen: the dot on the closed toggle. */
export function changedSince(
  view: TermsResponse,
  seen: SeenTerms | null,
): boolean {
  if (seen === null || seen.address === view.address) return false;
  return view.groups.some((group) =>
    group.terms.some((term) => flagOf(group.kind, term, seen) !== null),
  );
}

function isSeen(value: unknown): value is SeenTerms {
  if (typeof value !== "object" || value === null) return false;
  const { address, rows } = value as { address?: unknown; rows?: unknown };
  return (
    typeof address === "string" &&
    typeof rows === "object" &&
    rows !== null &&
    !Array.isArray(rows) &&
    Object.values(rows).every((words) => typeof words === "string")
  );
}

export function readSeen(taskId: string): SeenTerms | null {
  try {
    const stored = localStorage.getItem(STORE_PREFIX + taskId);
    if (stored === null) return null;
    const parsed: unknown = JSON.parse(stored);
    return isSeen(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function writeSeen(taskId: string, seen: SeenTerms): void {
  try {
    localStorage.setItem(STORE_PREFIX + taskId, JSON.stringify(seen));
  } catch {
    // Storage refused (a private window, a full quota): nothing is flagged next time.
  }
}

/**
 * The view as Generate's review showed it, in the words the server's lock reads
 * back: each served term's group, id, name, definition and numeral, in order,
 * under the view's address.
 */
export function shownTermsOf(view: TermsResponse): ShownTerms {
  return {
    address: view.address,
    words: view.groups.flatMap((group) =>
      group.terms.map((term) => ({
        kind: group.kind,
        thing_id: term.thing_id,
        name: term.name,
        definition: term.definition,
        numeral: term.numeral,
      })),
    ),
  };
}
